import type { LinePaidCost } from "@loot-os/fulfillment-core/order/stockx-size-refund";
import type { OrderStatus, OrderType } from "@loot-os/fulfillment-core/order/types";
import { Button } from "@loot-os/ui/components/button";
import { Checkbox } from "@loot-os/ui/components/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";

import {
  isShipTarget,
  shipButtonLabel,
  shipDialogTarget,
  transitionTargets,
} from "../lib/orderActions";
import {
  buildPaidCosts,
  type PaidAboveChargedRow,
  type PaidCostDollars,
  paidAboveCharged,
  readPaidAboveChargedRefusal,
} from "../lib/paidCosts";
import { useTRPC } from "../lib/trpc";
import { DisabledReason } from "./DisabledReason";
import { OrderCancelDialog } from "./OrderCancelDialog";
import { PaidAboveChargedWarning } from "./PaidAboveChargedWarning";
import { StockxPaidCostFields } from "./StockxPaidCostFields";

/** A StockX line the move to PROCESSING asks a paid cost for, with what the fulfiller reads it by. */
/** The visible line that says why Ship Order is held, which the button's wrapper points to. */
const SHIP_HOLD_REASON_ID = "ship-hold-reason";

export interface StockxPaidCostLine {
  id: string;
  label: string;
  /** What Loot OS stored as the line's charged price; a paid cost above its limit (`findPaidAboveCharged`) asks staff to confirm. */
  chargedPriceCents?: number;
  /** The US shipping the shopper paid inside the listing value, stored beside the charged price outside the US. */
  chargedUsShippingCents?: number;
  /** What the shopper paid over US shipping for the destination, stored beside the charged price. */
  chargedShippingSurchargeCents?: number;
}

/**
 * Walks an order along the state machine. `fromStatus` makes the write conflict rather than
 * skip a step when someone else moved it first. The move to PROCESSING also carries what Loot OS
 * paid StockX for each StockX line, which the size refund is computed from (ENG-7343). A paid
 * cost above a line's charged price stops the move until staff cancel the order as
 * PRICE_EXCEEDED or confirm the cost, which sends `isPaidAboveChargedConfirmed`. The page checks
 * first; a refusal the server answers with (`paid_cost_above_charged`) opens the same warning
 * from the lines it names.
 *
 * PRESHIPPED and SHIPPED are not buttons of their own: Retool's TCG Fulfill Order has one Ship
 * button ("Add Tracking" on a PRESHIPPED order) opening a dialog, and its confirm ships with a
 * tracking number or preships without one (`shipDialogTarget`), so a shipped order always has
 * one, which the server holds the dashboard to as well. The box starts from the saved tracking
 * number, the one a ship keeps (the server applies it too), so it takes no other; Skip Email
 * rides on the move to SHIPPED as the ship's plan for the tenant that owns the order; a locked
 * account is warned about, not refused, since Retool ships it; and `holdShipment` keeps the
 * button off until every card is packed (`packingHoldsShipment`). Request Review is dropped.
 *
 * Laid out as Retool's header buttons: the moves sit beside Ship Order at the top right, and the
 * reason Ship is held and the StockX paid costs (only while the move to PROCESSING asks for them)
 * go under the header, through `children`.
 */
export function OrderTransitionActions({
  order,
  stockxLines,
  holdShipment,
  children,
}: {
  order: {
    id: string;
    status: OrderStatus;
    type: OrderType;
    trackingNumber: string | null;
    accountLocked: boolean;
  };
  stockxLines: StockxPaidCostLine[];
  holdShipment: boolean;
  /**
   * Where the page puts the two parts: the buttons in its header row, the details (why Ship is
   * held, the StockX paid costs and their warning) beneath it, so a form never grows the header.
   * `details` is null when there is nothing to say.
   */
  children: (parts: { buttons: ReactNode; details: ReactNode }) => ReactNode;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [typedTrackingNumber, setTypedTrackingNumber] = useState("");
  const [skipEmail, setSkipEmail] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const [paidDollarsByLineId, setPaidDollarsByLineId] = useState<Record<string, PaidCostDollars>>(
    {},
  );
  // The lines whose cost stopped the move to PROCESSING. Typing a cost clears them, and Confirm
  // sends the costs typed now, for the StockX lines the order has now.
  const [aboveChargedRows, setAboveChargedRows] = useState<PaidAboveChargedRow[] | null>(null);

  const targets = transitionTargets(order.status, order.type);
  const asksPaidCosts = targets.includes("PROCESSING") && stockxLines.length > 0;
  const stockxLineIds = new Set(stockxLines.map((line) => line.id));
  // What the failure handlers read: the query client's toast check runs before onError, and
  // both ask about the same failure, so the refusal is parsed once per error. The layout effect
  // writes it after each commit, so a render React discards never reaches the handlers.
  const warningContext = useRef({ asksPaidCosts, stockxLineIds });
  useLayoutEffect(() => {
    warningContext.current = { asksPaidCosts, stockxLineIds };
  });
  const lastWarning = useRef<{
    error: unknown;
    rows: PaidAboveChargedRow[] | null;
  } | null>(null);
  /** The rows the warning will show for a failure, or null when it shows none and the toast must. */
  function warningRowsFor(error: unknown): PaidAboveChargedRow[] | null {
    const last = lastWarning.current;
    if (last && last.error === error) return last.rows;
    const { asksPaidCosts: asks, stockxLineIds: ids } = warningContext.current;
    const shown = readPaidAboveChargedRefusal(error)?.filter((row) => ids.has(row.lineId));
    const rows = asks && shown?.length ? shown : null;
    lastWarning.current = { error, rows };
    return rows;
  }

  const transition = useMutation(
    trpc.platform.fulfillment.orders.transition.mutationOptions({
      // The warning replaces the toast only when it shows a row; any other failure toasts.
      meta: { showsError: (error) => warningRowsFor(error) !== null },
      onError: (error) => {
        const rows = warningRowsFor(error);
        if (rows) setAboveChargedRows(rows);
      },
      onSuccess: (updated) => {
        setShipOpen(false);
        toast.success(`Order is ${updated.status}.`);
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.orders.pathKey(),
        });
      },
    }),
  );

  if (targets.length === 0) return null;
  const stepTargets = targets.filter((target) => !isShipTarget(target));
  const shipLabel = shipButtonLabel(order.status, targets);
  // A blank saved value is no number, as the server's resolveShipTrackingNumber reads it.
  const savedTrackingNumber = order.trackingNumber?.trim() ? order.trackingNumber : null;
  const trackingNumber = savedTrackingNumber ?? typedTrackingNumber;
  const shipTarget = shipDialogTarget({ targets, trackingNumber });
  // A line cancelled since the refusal drops out of the warning.
  const shownAboveChargedRows = aboveChargedRows?.filter((row) => stockxLineIds.has(row.lineId));

  /** What every move sends; only a move to PROCESSING carries paid costs or the confirm. */
  function transitionInput(
    target: (typeof targets)[number],
    {
      paidCosts,
      isPaidAboveChargedConfirmed,
    }: { paidCosts?: LinePaidCost[]; isPaidAboveChargedConfirmed?: true } = {},
  ) {
    return {
      id: order.id,
      toStatus: target,
      fromStatus: order.status,
      trackingNumber: trackingNumber.trim() || undefined,
      paidCosts,
      isPaidAboveChargedConfirmed,
      // A preship sends no plan: the server refuses one on any move but SHIPPED.
      notifyCustomer: target === "SHIPPED" ? !skipEmail : undefined,
    };
  }

  /** One paid cost per current StockX line from the amounts typed now, or null after a toast. */
  function enteredPaidCosts(): LinePaidCost[] | null {
    const entered = buildPaidCosts(
      stockxLines.map((line) => line.id),
      paidDollarsByLineId,
    );
    if (entered === null) {
      toast.error(
        "Enter Your Purchase Price (above $0.00), Marketplace Fee and Shipping for every StockX line, in dollars.",
      );
    }
    return entered;
  }

  /** Submit the move and tracking number; require valid dollar amounts for StockX processing. */
  function move(target: (typeof targets)[number]) {
    let paidCosts: LinePaidCost[] | undefined;
    if (target === "PROCESSING" && stockxLines.length > 0) {
      const entered = enteredPaidCosts();
      if (entered === null) return;
      const rows = paidAboveCharged(stockxLines, entered);
      if (rows.length > 0) {
        setAboveChargedRows(rows);
        return;
      }
      paidCosts = entered;
    }
    transition.mutate(transitionInput(target, { paidCosts }));
  }

  /** Staff confirm the costs above the charged price: the move goes on with the costs typed now. */
  function confirmAboveCharged() {
    const paidCosts = enteredPaidCosts();
    if (paidCosts === null) return;
    transition.mutate(
      transitionInput("PROCESSING", {
        paidCosts,
        isPaidAboveChargedConfirmed: true,
      }),
    );
  }

  const holdText = "Pack every card before this TCG order is preshipped or shipped.";
  const buttons = (
    <div className="flex flex-wrap justify-end gap-2">
      {stepTargets.map((target) => (
        <Button
          key={target}
          type="button"
          variant="outline"
          disabled={transition.isPending}
          onClick={() => move(target)}
        >
          {target}
        </Button>
      ))}
      {shipLabel ? (
        <Dialog open={shipOpen} onOpenChange={setShipOpen}>
          <DisabledReason
            reason={holdShipment ? holdText : undefined}
            describedBy={SHIP_HOLD_REASON_ID}
          >
            <DialogTrigger
              render={<Button type="button" />}
              disabled={transition.isPending || holdShipment}
            >
              {shipLabel === "Ship" ? "Ship Order" : shipLabel}
            </DialogTrigger>
          </DisabledReason>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{shipLabel === "Ship" ? "Ship order" : "Add tracking"}</DialogTitle>
              <DialogDescription>
                {shipLabel === "Ship"
                  ? "With a tracking number the order ships; with none it is preshipped, and Add Tracking ships it later."
                  : "Enter the tracking number to ship this preshipped order."}
              </DialogDescription>
            </DialogHeader>
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (shipTarget) move(shipTarget);
              }}
            >
              <div className="grid gap-2">
                <Label htmlFor="tracking-number">Tracking number</Label>
                <Input
                  id="tracking-number"
                  value={trackingNumber}
                  readOnly={savedTrackingNumber !== null}
                  disabled={transition.isPending}
                  onChange={(e) => setTypedTrackingNumber(e.target.value)}
                />
                {savedTrackingNumber !== null ? (
                  <p className="text-sm text-muted-foreground">
                    The order's saved tracking number is the one a ship keeps, as in Retool. Change
                    it in the order's Tracking field.
                  </p>
                ) : null}
              </div>
              <div className="grid gap-1">
                <Label className="flex items-center gap-2 font-normal">
                  <Checkbox
                    checked={skipEmail}
                    disabled={transition.isPending || shipTarget !== "SHIPPED"}
                    onCheckedChange={(checked) => setSkipEmail(checked === true)}
                  />
                  Skip email
                </Label>
                <p className="text-sm text-muted-foreground">
                  On a ship, asks the tenant that owns the order not to email the customer that it
                  shipped. The in-app notification still goes out. A preship sends no email.
                </p>
              </div>
              {order.accountLocked ? (
                <p className="text-sm font-bold text-destructive">
                  The account is locked. Retool ships it anyway; check before you ship.
                </p>
              ) : null}
              <DialogFooter>
                <DialogClose render={<Button type="button" variant="outline" />}>
                  Cancel
                </DialogClose>
                <Button type="submit" disabled={transition.isPending || shipTarget === null}>
                  {shipTarget === "PRESHIPPED" ? "Preship" : "Ship"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );

  const holdsShip = holdShipment && shipLabel !== null;
  const details =
    holdsShip || asksPaidCosts ? (
      <div className="flex flex-col items-end gap-3">
        {holdsShip ? (
          <p id={SHIP_HOLD_REASON_ID} className="text-sm text-muted-foreground">
            {holdText}
          </p>
        ) : null}
        {asksPaidCosts ? (
          <div className="grid w-full max-w-sm gap-3 text-left">
            <p className="text-sm text-muted-foreground">
              For each line, type the amounts the StockX order details page shows for the size
              actually bought. The size refund is worked out from them on the move to PROCESSING;
              shipping never enters it.
            </p>
            {stockxLines.map((line) => (
              <StockxPaidCostFields
                key={line.id}
                lineId={line.id}
                label={line.label}
                dollars={paidDollarsByLineId[line.id] ?? {}}
                disabled={transition.isPending}
                onChange={(key, value) => {
                  setAboveChargedRows(null);
                  setPaidDollarsByLineId((current) => ({
                    ...current,
                    [line.id]: { ...current[line.id], [key]: value },
                  }));
                }}
              />
            ))}
          </div>
        ) : null}
        {asksPaidCosts && shownAboveChargedRows?.length ? (
          <PaidAboveChargedWarning
            rows={shownAboveChargedRows}
            labelsByLineId={Object.fromEntries(stockxLines.map((line) => [line.id, line.label]))}
            isPending={transition.isPending}
            onConfirm={confirmAboveCharged}
            cancelAction={<OrderCancelDialog orderId={order.id} orderType={order.type} />}
          />
        ) : null}
      </div>
    ) : null;

  return children({ buttons, details });
}
